// Pure helpers for the case × target matrix: merge a polled iteration row with
// its live stream state into one cell status.
import type { SphinxIteration } from "@/lib/sphinx-api";
import type { Grade, RunStatus } from "@/shared/sphinx";
import type { LiveIteration } from "./sphinx-run-stream-reducer";

export type MatrixCellKind =
  | "pending"
  | "running"
  | "passed"
  | "failed"
  | "error"
  | "canceled"
  /**
   * The iteration ran, but the case asserted nothing — no `expectedToolCalls`,
   * no `expectedOutput`, no `judgeRubric` — so the matcher passed vacuously
   * (`Grade.ungraded`, set in `server/grading/index.ts`). Neutral on purpose:
   * a bare "Testing" case scoring a green pass on claude-desktop read as a
   * real result, which is exactly what the Evaluate surface must not do. Every
   * Playground dispatch lands here too.
   */
  | "ungraded";

export type MatrixCellStatus = {
  kind: MatrixCellKind;
  label: string;
  /** Error text for `error` cells (shown on hover). */
  error?: string;
  /** Judge reasons / matcher misses for the hover — the WHY behind the label. */
  detail?: string;
};

const TERMINAL: ReadonlySet<string> = new Set(["complete", "canceled", "failed"]);
export const isTerminalStatus = (s: string | null | undefined) =>
  s != null && TERMINAL.has(s);

export function gradeOf(value: unknown): Grade | null {
  if (!value || typeof value !== "object") return null;
  const g = value as Partial<Grade>;
  return typeof g.passed === "boolean" ? (g as Grade) : null;
}

/**
 * Effective (status, grade, error) for a cell. The polled row wins once it is
 * terminal; before that the stream may be ahead (running / early finish).
 */
export function mergeIteration(
  row: Pick<SphinxIteration, "status" | "grade" | "error">,
  live?: LiveIteration
): { status: RunStatus; grade: Grade | null; error?: string } {
  const status: RunStatus = isTerminalStatus(row.status)
    ? row.status
    : (live?.status ?? row.status);
  const grade = gradeOf(row.grade) ?? live?.grade ?? null;
  // The row's own error column is where a failure's reason actually lives
  // ("orphaned: runner restarted…", "agent stalled: …") — grade/live only
  // cover grading failures and the currently-attached SSE tail. Without it,
  // a failure viewed after the fact rendered as a bare "failed".
  const error = grade?.error ?? live?.error ?? row.error ?? undefined;
  return { status, grade, error };
}

export function matrixCellStatus(
  row: Pick<SphinxIteration, "status" | "grade">,
  live?: LiveIteration
): MatrixCellStatus {
  const { status, grade, error } = mergeIteration(row, live);
  switch (status) {
    case "running":
      return { kind: "running", label: "running" };
    case "canceled":
      return { kind: "canceled", label: "canceled" };
    case "failed":
      return { kind: "error", label: "error", error: error ?? "failed" };
    case "complete": {
      // Checked BEFORE pass/fail: `ungraded` rides on a `passed: true` grade,
      // so reading `passed` first is what made a vacuous pass look green.
      if (grade?.ungraded === true) return { kind: "ungraded", label: "ran" };
      // The judge's word beats a bare matcher verdict on the cell itself: a
      // "fail" that was really "judge 0.15: dispatched the wrong project" and
      // one that was "matcher: run_video_agent missing" are different findings,
      // and the scoreboard kept flattening them into the same red chip.
      const judgeVerdict = grade?.judge ?? null;
      const pct =
        judgeVerdict && typeof judgeVerdict.score === "number"
          ? `${Math.round(judgeVerdict.score * 100)}%`
          : null;
      const reasons =
        judgeVerdict && judgeVerdict.reasons.length > 0
          ? judgeVerdict.reasons.join("\n")
          : undefined;
      if (grade?.passed === true)
        return {
          kind: "passed",
          label: pct ? `pass · ${pct}` : "pass",
          detail: reasons,
        };
      if (grade?.passed === false) {
        // env/infra kinds (credit denial, permission block, unexecuted call,
        // timeout) belong on the chip — they are not model findings.
        const kindTag = grade.kind ? ` · ${grade.kind}` : "";
        if (judgeVerdict?.verdict === "fail")
          return {
            kind: "failed",
            label: `fail · judge ${pct ?? ""}${kindTag}`.replace("  ", " ").trimEnd(),
            error,
            detail: reasons,
          };
        const matcher = grade.matcher as Grade["matcher"] | null;
        if (matcher && matcher.passed === false) {
          const parts = [
            matcher.missing.length > 0 ? `missing: ${matcher.missing.join(", ")}` : null,
            matcher.unexpected.length > 0 ? `unexpected: ${matcher.unexpected.join(", ")}` : null,
          ].filter(Boolean);
          return {
            kind: "failed",
            label: `fail · matcher${kindTag}`,
            error,
            detail: [parts.join("; ") || null, reasons].filter(Boolean).join("\n") || undefined,
          };
        }
        // Neither channel failed on its own — a suspect/tainted grade.
        return { kind: "failed", label: `fail${kindTag}`, error, detail: reasons };
      }
      return { kind: "pending", label: "grading" };
    }
    default:
      return live?.queuePosition
        ? { kind: "pending", label: `queued #${live.queuePosition}` }
        : { kind: "pending", label: "pending" };
  }
}

/**
 * An `ungraded` cell counts as NEITHER passed nor failed: it asserted nothing,
 * so folding it into `passed` would inflate a pass rate with iterations that
 * only proved the client answered at all.
 */
export function summarizeCells(cells: MatrixCellStatus[]) {
  let passed = 0;
  let failed = 0;
  let ungraded = 0;
  for (const c of cells) {
    if (c.kind === "passed") passed += 1;
    else if (c.kind === "failed" || c.kind === "error") failed += 1;
    else if (c.kind === "ungraded") ungraded += 1;
  }
  return { total: cells.length, passed, failed, ungraded };
}

/** Why an `ungraded` cell is neither pass nor fail. One sentence, for tooltips. */
export const UNGRADED_CELL_EXPLANATION =
  "Ran, but this case asserts nothing — no expected tool calls, expected output, or judge rubric — so there is nothing to pass or fail.";

/**
 * One run can hold several iterations of the SAME client at different models
 * (`iterations.model`, migration 012 — a client with two models enabled runs
 * both), so a row keyed by client alone collides. Key on (client, model).
 */
export const clientModelKey = (
  target: string,
  model: string | null | undefined
): string => `${target} ${model ?? ""}`;
